import {useCallback} from 'react';

import {useApolloClient, useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {
    EditAccountBusinessCardField as EditAccountBusinessCardFieldMutation,
    EditAccountBusinessCardFieldVariables,
    GetPeepBusinessCardInfo as GetPeepBusinessCardInfoQuery,
    GetPeepBusinessCardInfoVariables,
    EavPatchInput,
} from 'nutshell-graphql-types';

import {getOptimisticEavPatchResponse} from '../helpers/get-optimistic-peep-for-patch';
import EDIT_ACCOUNT_BUSINESS_CARD_FIELD from '../mutations/edit-account-business-card-field.graphql';
import GET_PEEP_BUSINESS_CARD_INFO from '../queries/get-peep-business-card-info.graphql';

export function useEditAccountBusinessCardField(): {
    editAccountBusinessCardField: (input: EavPatchInput) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} {
    const client = useApolloClient();
    const [editAccountBusinessCardFieldMutation, {loading, error}] = useMutation<
        EditAccountBusinessCardFieldMutation,
        EditAccountBusinessCardFieldVariables
    >(EDIT_ACCOUNT_BUSINESS_CARD_FIELD);

    const editAccountBusinessCardField = useCallback(
        (input: EavPatchInput) => {
            const cached = client.readQuery<
                GetPeepBusinessCardInfoQuery,
                GetPeepBusinessCardInfoVariables
            >({
                query: GET_PEEP_BUSINESS_CARD_INFO,
                variables: {
                    entityId: input.entityId,
                },
            });

            if (!cached?.peep || typeof input.name !== 'string') {
                return editAccountBusinessCardFieldMutation({variables: {input}});
            }

            const optimisticResponse = getOptimisticEavPatchResponse(
                cached.peep,
                input.attributeValueId,
                input.name,
                'account'
            );

            return editAccountBusinessCardFieldMutation({
                variables: {input},
                optimisticResponse,
            });
        },
        [client, editAccountBusinessCardFieldMutation]
    );

    return {
        editAccountBusinessCardField,
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
}
